import { useCallback, useEffect, useRef, useState } from "react";

import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ReplayIcon from "@mui/icons-material/Replay";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

const NAVY = "#0e3461";
const MAX_EDGE = 1400;
/** Guide only — helps the payer centre their face; the full frame is saved. */
const OVAL_WIDTH_RATIO = 0.68;
const OVAL_HEIGHT_RATIO = 0.78;

/**
 * Live photo of the payer, used by staff to confirm the person who signed is
 * the person in the passport. File upload is intentionally not offered: a
 * saved image is too easy to substitute. Camera access is required to continue.
 */
export default function SelfieCapture({ t, busy, progress, error, onConfirm }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const [streaming, setStreaming] = useState(false);
  const [starting, setStarting] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [preview, setPreview] = useState(null);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setStreaming(false);
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    setStarting(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "user" }, width: { ideal: 1280 } },
        audio: false,
      });
      streamRef.current = stream;
      setStreaming(true);
      // The <video> only exists once streaming flips on, so attach after paint.
      requestAnimationFrame(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      });
    } catch {
      setCameraError(t("selfieCameraBlocked"));
    } finally {
      setStarting(false);
    }
  }, [t]);

  useEffect(() => {
    if (preview) return undefined;
    startCamera();
    return stopCamera;
  }, [preview, startCamera, stopCamera]);

  const shoot = () => {
    const video = videoRef.current;
    if (!video?.videoWidth) return;

    // Save the full camera frame the user sees (object-fit:cover, un-mirrored).
    // The dashed oval is only a guide for centering — it is not cropped out.
    const frameW = 900;
    const frameH = 1200;
    const scale = Math.min(1, MAX_EDGE / Math.max(frameW, frameH));
    const out = document.createElement("canvas");
    out.width = Math.round(frameW * scale);
    out.height = Math.round(frameH * scale);
    const ctx = out.getContext("2d");

    const videoRatio = video.videoWidth / video.videoHeight;
    const frameRatio = out.width / out.height;
    let drawW;
    let drawH;
    if (videoRatio > frameRatio) {
      drawH = out.height;
      drawW = out.height * videoRatio;
    } else {
      drawW = out.width;
      drawH = out.width / videoRatio;
    }
    const drawX = (out.width - drawW) / 2;
    const drawY = (out.height - drawH) / 2;

    ctx.translate(out.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, drawX, drawY, drawW, drawH);

    out.toBlob(
      (blob) => {
        if (!blob) return;
        stopCamera();
        setPreview({ blob, url: URL.createObjectURL(blob) });
      },
      "image/jpeg",
      0.86
    );
  };

  const retake = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const shownError = cameraError || error;

  return (
    <Stack spacing={1.5} alignItems="center">
      <Stack spacing={0.5} alignItems="center" sx={{ textAlign: "center" }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: NAVY, fontSize: { xs: 17, sm: 18 } }}
        >
          {t("selfieHeading")}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            fontSize: 13,
            lineHeight: 1.45,
            maxWidth: 460,
          }}
        >
          {t("selfieIntro")}
        </Typography>
      </Stack>

      <Box
        sx={{
          width: "100%",
          px: 1.5,
          py: 1,
          borderRadius: 2,
          bgcolor: "#f6f7fb",
          border: "1px solid",
          borderColor: "grey.200",
        }}
      >
        <Typography variant="subtitle2" sx={{ color: NAVY, mb: 0.25, fontSize: 12.5 }}>
          {t("selfieChecklistTitle")}
        </Typography>
        <Stack component="ul" spacing={0.15} sx={{ pl: 2, m: 0 }}>
          {[t("selfieCheck1"), t("selfieCheck2"), t("selfieCheck3")].map((line) => (
            <Typography
              key={line}
              component="li"
              variant="body2"
              sx={{ color: "text.secondary", fontSize: 12.5, lineHeight: 1.4 }}
            >
              {line}
            </Typography>
          ))}
        </Stack>
      </Box>

      <Box
        sx={{
          position: "relative",
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "#101623",
          aspectRatio: "3 / 4",
          width: "100%",
          maxWidth: { xs: 180, sm: 210 },
          maxHeight: { xs: 240, sm: 280 },
          mx: "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {preview ? (
          <Box
            component="img"
            src={preview.url}
            alt=""
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center center",
            }}
          />
        ) : streaming ? (
          <>
            <Box
              component="video"
              ref={videoRef}
              playsInline
              muted
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center center",
                transform: "scaleX(-1)",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                width: "68%",
                height: "78%",
                transform: "translate(-50%, -50%)",
                border: "2px dashed rgba(255,255,255,0.7)",
                borderRadius: "50%",
                pointerEvents: "none",
              }}
            />
          </>
        ) : (
          <Stack spacing={0.75} alignItems="center" sx={{ color: "grey.500", px: 2 }}>
            {starting ? (
              <CircularProgress size={22} sx={{ color: "grey.400" }} />
            ) : (
              <CameraAltOutlinedIcon sx={{ fontSize: 32 }} />
            )}
            <Typography variant="body2" sx={{ textAlign: "center", fontSize: 12 }}>
              {starting ? t("selfieStarting") : t("selfiePlaceholder")}
            </Typography>
          </Stack>
        )}
      </Box>

      {shownError && (
        <Alert severity="error" sx={{ borderRadius: 2, width: "100%", py: 0.5 }}>
          {shownError}
        </Alert>
      )}

      {busy ? (
        <Stack spacing={1} alignItems="center" sx={{ py: 1, width: "100%" }}>
          <CircularProgress size={28} />
          <Typography variant="subtitle2">{t("selfieUploading")}</Typography>
          {progress > 0 && progress < 100 && (
            <Box sx={{ width: "100%", maxWidth: 280 }}>
              <LinearProgress variant="determinate" value={progress} />
            </Box>
          )}
        </Stack>
      ) : (
        <Stack spacing={1} sx={{ width: "100%" }}>
          {preview ? (
            <>
              <Button
                fullWidth
                size="large"
                variant="contained"
                startIcon={<CheckCircleOutlineIcon />}
                onClick={() => onConfirm(preview.blob)}
                sx={{ py: 1, borderRadius: 2, textTransform: "none", fontSize: 15 }}
              >
                {t("selfieUseCta")}
              </Button>
              <Button
                fullWidth
                size="large"
                variant="outlined"
                color="secondary"
                startIcon={<ReplayIcon />}
                onClick={retake}
                sx={{ py: 1, borderRadius: 2, textTransform: "none", fontSize: 15 }}
              >
                {t("selfieRetakeCta")}
              </Button>
            </>
          ) : streaming ? (
            <Button
              fullWidth
              size="large"
              variant="contained"
              startIcon={<CameraAltOutlinedIcon />}
              onClick={shoot}
              sx={{ py: 1, borderRadius: 2, textTransform: "none", fontSize: 15 }}
            >
              {t("selfieShootCta")}
            </Button>
          ) : (
            <Button
              fullWidth
              size="large"
              variant="contained"
              disabled={starting}
              startIcon={<CameraAltOutlinedIcon />}
              onClick={startCamera}
              sx={{ py: 1, borderRadius: 2, textTransform: "none", fontSize: 15 }}
            >
              {t("selfieStartCta")}
            </Button>
          )}
        </Stack>
      )}

      <Typography
        variant="caption"
        sx={{ color: "text.disabled", textAlign: "center", lineHeight: 1.4, fontSize: 11 }}
      >
        {t("selfiePrivacy")}
      </Typography>
    </Stack>
  );
}
